import * as React from 'react';
import { atom, selectorFamily, useSetRecoilState } from 'recoil';
export const MAX_VISITED_ITEMS = 6;

/**
 * Atom to keep track of the recently visited content
 * as a map of organization IDs to arrays of spaces and sites IDs.
 */
const STORAGE_KEY = '@recoil/recentlyVisitedContentAtom';

export const recentlyVisitedContentAtom = atom({
    key: 'recentlyVisitedContentAtom',
    default: {},
    effects: [syncWithLocalStorageRecoilEffect(STORAGE_KEY)],
});

// Sync recoil's atom with localStorage
function syncWithLocalStorageRecoilEffect(key: string) {
    return ({ setSelf, onSet }) => {
        const savedValue = localStorage.getItem(key);

        if (savedValue != null) {
            setSelf(JSON.parse(savedValue));
        }

        // Listen to changes to the atom and persist to localStorage
        onSet((newValue) => {
            localStorage.setItem(key, JSON.stringify(newValue));
        });
    };
}

/**
 * Selector family to manage state of recently visited content per organization.
 */
export const recentlyVisitedContentSelector = selectorFamily<string[], string>({
    key: 'recentlyVisitedContentSelector',
    get:
        (orgId) =>
        ({ get }) => {
            const recentlyVisitedContent = get(recentlyVisitedContentAtom);

            if (recentlyVisitedContent[orgId]) {
                // If new key already exists, migration has already been done.
                return recentlyVisitedContent[orgId].slice(0, MAX_VISITED_ITEMS);
            }

            return [];
        },
    set:
        (orgId) =>
        ({ set, get }, newValue) => {
            const recentlyVisitedContent = get(recentlyVisitedContentAtom);

            const updatedLastVisitedContent = {
                ...recentlyVisitedContent,
                // We have to check newValue's type because it's string[] | DefaultValue
                [orgId]: Array.isArray(newValue) ? newValue.slice(0, MAX_VISITED_ITEMS) : [],
            };

            set(recentlyVisitedContentAtom, updatedLastVisitedContent);
        },
});

/**
 * Use to mark a space or site as recently visited.
 */
export function useMarkContentAsRecentlyVisited({
    contentId,
    organizationId,
}: {
    contentId: string;
    organizationId: string;
}) {
    const setRecentlyVisitedContent = useSetRecoilState(
        recentlyVisitedContentSelector(organizationId)
    );

    React.useEffect(() => {
        setRecentlyVisitedContent((prev) => {
            // Remove the contentId if it already exists, then add it to the top
            const updatedContentIds = [contentId, ...(prev || []).filter((id) => id !== contentId)];
            return updatedContentIds.slice(0, MAX_VISITED_ITEMS);
        });
    }, [contentId, setRecentlyVisitedContent]);
}
